import { Box, Button, Checkbox, FormControlLabel, FormGroup, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import AppSimpleDialog from 'app/shared-components/dialogs/AppSimpleDialog';
import { useForm, Controller } from 'react-hook-form';
import { completeGetStarted, updateMerchant } from 'src/app/api/MerchantApi';
import { useCurrentMerchant } from 'src/app/queries/merchant.queries';
import IQueryKeys from 'src/app/types/IQueryKeys';
import { ModalComponent } from './modalManager';

interface FormData {
  interestedInCryptocurrency: boolean;
  interestedInCashlessATM: boolean;
  interestedInACH: boolean;
}

const industryCapabilities = [
  {
    id: 'interestedInCashlessATM',
    label: 'Cashless ATM',
    description: 'Point-of-banking solutions for in-store checkout.',
  },
  {
    id: 'interestedInCryptocurrency',
    label: 'Cryptocurrency',
    description: 'Accept crypto with optional settlement to USD.',
  },
  {
    id: 'interestedInACH',
    label: 'ACH',
    description: 'Direct bank transfers for invoices and high-ticket sales.',
  },
];

export const Merchant0420CapabilitiesModal: ModalComponent = ({ open, onClose }) => {
  const currentUser = useCurrentMerchant();
  const queryClient = useQueryClient();

  const {
    control,
    handleSubmit,
    watch,
    formState: { isSubmitting },
  } = useForm<FormData>({
    defaultValues: {
      interestedInCryptocurrency: false,
      interestedInCashlessATM: false,
      interestedInACH: false,
    },
  });

  const watchedValues = watch();
  const hasSelectedCapabilities = Object.values(watchedValues).some(value => value === true);

  const onSubmit = async (data: FormData) => {
    if (!hasSelectedCapabilities || !currentUser.data) return;

    try {
      await updateMerchant(data, currentUser.data.data.id);
      await completeGetStarted();
      await queryClient.refetchQueries({ queryKey: [IQueryKeys.merchantInfoKey] });
      onClose();
    } catch (error) {
      console.error('Error saving industry capabilities:', error);
    }
  };

  const isSubmitDisabled = !hasSelectedCapabilities || isSubmitting;

  return (
    <AppSimpleDialog
      open={open}
      handleClose={() => { }} // Prevent closing until submission
      title="Tell us about your industry needs"
      size="md"
      scroll="paper"
      maxWidth="sm"
      hideCloseButton={true}
      disableEscapeKeyDown={true}
      onClose={() => { }} // Prevent closing until submission
    >
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
          We'll route you to partners that support industry‑specific capabilities.
        </Typography>

        <Typography variant="body1" sx={{ mb: 3, fontWeight: 500 }}>
          Which industry‑specific capabilities are you interested in? Select all that apply.
        </Typography>

        <form onSubmit={handleSubmit(onSubmit)}>
          <FormGroup sx={{ mb: 3 }}>
            {industryCapabilities.map((capability) => (
              <Controller
                key={capability.id}
                name={capability.id as keyof FormData}
                control={control}
                render={({ field: { onChange, value } }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={value}
                        onChange={(e) => onChange(e.target.checked)}
                        size="medium"
                        disableRipple
                        sx={{
                          mt: 0.5,
                          '&.Mui-checked': {
                            color: '#6366f1',
                          },
                        }}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.4 }}>
                          {capability.label}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {capability.description}
                        </Typography>
                      </Box>
                    }
                    sx={{ mb: 3, alignItems: 'flex-start' }}
                  />
                )}
              />
            ))}
          </FormGroup>

          <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', fontStyle: 'italic' }}>
            Your selection helps us match you with suitable providers.
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="submit"
              variant="contained"
              className="flex items-center text-15 text-white font-500 bg-blue-primary hover:bg-blue-primary-hover px-16 py-8 mt-8 sm:mt-0"
              disabled={isSubmitDisabled}
            >
              {isSubmitting ? 'Saving...' : 'Continue'}
            </Button>
          </Box>
        </form>
      </Box>
    </AppSimpleDialog>
  );
};
